import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiHelpers } from '../services/api';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  MagnifyingGlassIcon,
  BookOpenIcon,
  QuestionMarkCircleIcon,
  ChevronRightIcon,
  SparklesIcon,
  TicketIcon,
  ArrowLeftIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  RocketLaunchIcon,
  CircleStackIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import SubmitTicketModal from '../components/tickets/SubmitTicketModal';

// Custom ReactMarkdown components for YouTube embedding
const markdownComponents = {
  // Auto-convert YouTube links to embedded videos
  a: ({ href, children, ...props }) => {
    // Match YouTube URLs (youtube.com/watch?v=, youtu.be/, youtube.com/embed/)
    const youtubeMatch = href?.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/
    );
    
    if (youtubeMatch) {
      const videoId = youtubeMatch[1];
      return (
        <div className="my-4 aspect-video max-w-2xl">
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${videoId}`}
            title={typeof children === 'string' ? children : 'YouTube video'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="rounded-lg"
          />
        </div>
      );
    }
    
    // Regular links open in new tab if external
    const isExternal = href?.startsWith('http');
    return (
      <a 
        href={href} 
        {...props}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    );
  },
  // Style iframes (for raw HTML YouTube embeds)
  iframe: ({ node, ...props }) => (
    <div className="my-4 aspect-video max-w-2xl">
      <iframe 
        {...props} 
        className="rounded-lg w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      />
    </div>
  )
};

const CATEGORY_ICONS = {
  getting_started: RocketLaunchIcon,
  data_sources: CircleStackIcon,
  dashboards: ChartBarIcon,
  ai_assistant: ChatBubbleLeftRightIcon,
  billing: CreditCardIcon,
  troubleshooting: WrenchScrewdriverIcon
};

const CATEGORY_COLORS = {
  getting_started: 'text-emerald-500',
  data_sources: 'text-blue-500',
  dashboards: 'text-purple-500',
  ai_assistant: 'text-pink-500',
  billing: 'text-amber-500',
  troubleshooting: 'text-red-500'
};

export default function Help() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [categories, setCategories] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docLoading, setDocLoading] = useState(false);
  
  // Search/Ask state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [aiAnswer, setAIAnswer] = useState(null);
  const [askLoading, setAskLoading] = useState(false);
  const [aiUsageInfo, setAIUsageInfo] = useState(null); // { remaining, limit }
  
  // Ticket modal
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  
  // Get current doc slug from URL
  const currentSlug = searchParams.get('doc');
  
  useEffect(() => {
    loadDocuments();
  }, []);
  
  useEffect(() => {
    if (currentSlug) {
      loadDocument(currentSlug);
    } else {
      setSelectedDoc(null);
    }
  }, [currentSlug]);
  
  const loadDocuments = async () => {
    try {
      const response = await apiHelpers.getHelpDocs();
      setCategories(response.data.categories || []);
    } catch (error) {
      toast.error('Failed to load documentation');
    } finally {
      setLoading(false);
    }
  };
  
  const loadDocument = async (slug) => {
    setDocLoading(true);
    try {
      const response = await apiHelpers.getHelpDoc(slug);
      setSelectedDoc(response.data.document);
      setFeedbackGiven(prev => ({ ...prev, [slug]: false }));
    } catch (error) {
      toast.error('Document not found');
      setSearchParams({});
    } finally {
      setDocLoading(false);
    }
  };
  
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    try {
      const response = await apiHelpers.searchHelpDocs(searchQuery);
      setSearchResults(response.data.results);
      setAIAnswer(null);
    } catch (error) {
      toast.error('Search failed');
    }
  };
  
  const handleAskAI = async () => {
    if (!searchQuery.trim()) return;
    
    setAskLoading(true);
    try {
      const response = await apiHelpers.askHelpQuestion(searchQuery);
      setAIAnswer(response.data);
      setSearchResults(null);
      
      // Update usage info if provided
      if (response.data.remaining !== undefined) {
        setAIUsageInfo({
          remaining: response.data.remaining,
          limit: response.data.limit,
          cached: response.data.cached
        });
      }
    } catch (error) {
      // Handle rate limit error (429)
      if (error.response?.status === 429) {
        const data = error.response.data;
        toast.error(data.message || 'Daily AI question limit reached');
        setAIUsageInfo({
          remaining: 0,
          limit: data.limit,
          resetAt: data.resetAt
        });
      } else {
        toast.error('Failed to get AI answer');
      }
    } finally {
      setAskLoading(false);
    }
  };
  
  const handleDocClick = (slug) => {
    setSearchParams({ doc: slug });
    setSearchResults(null);
    setAIAnswer(null);
    setSearchQuery('');
  };
  
  const handleFeedback = async (isHelpful) => {
    if (!selectedDoc || feedbackGiven[selectedDoc.slug]) return;
    
    try {
      await apiHelpers.recordHelpFeedback(selectedDoc.slug, isHelpful);
      setFeedbackGiven(prev => ({ ...prev, [selectedDoc.slug]: true }));
      toast.success('Thanks for your feedback!');
    } catch (error) {
      console.error('Failed to record feedback:', error);
    }
  };
  
  const renderDocumentContent = () => {
    if (docLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
    
    if (!selectedDoc) {
      return (
        <div className="text-center py-20">
          <BookOpenIcon className="w-16 h-16 text-surface-300 dark:text-surface-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-surface-700 dark:text-surface-300 mb-2">
            Select a topic to get started
          </h2>
          <p className="text-surface-500 dark:text-surface-400">
            Browse categories on the left or use the search bar above
          </p>
        </div>
      );
    }
    
    const CategoryIcon = CATEGORY_ICONS[selectedDoc.category] || DocumentTextIcon;
    
    return (
      <div className="animate-fade-in p-5">
        {/* Breadcrumb */}
        <button
          onClick={() => setSearchParams({})}
          className="flex items-center gap-2 text-sm text-surface-500 hover:text-primary-600 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to all topics
        </button>
        
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className={`p-2 rounded-lg bg-surface-100 dark:bg-surface-800 ${CATEGORY_COLORS[selectedDoc.category]}`}>
            <CategoryIcon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-900 dark:text-white">
              {selectedDoc.title}
            </h1>
            <p className="text-sm text-surface-500 capitalize">
              {selectedDoc.category?.replace('_', ' ')}
            </p>
          </div>
        </div>
        
        {/* Content */}
        <div className="prose dark:prose-invert max-w-none">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]} 
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          >
            {selectedDoc.content}
          </ReactMarkdown>
        </div>
        
        {/* Feedback */}
        <div className="mt-8 pt-6 border-t border-surface-200 dark:border-surface-700">
          <div className="flex items-center justify-between">
            <p className="text-surface-600 dark:text-surface-400">
              Was this helpful?
            </p>
            {feedbackGiven[selectedDoc.slug] ? (
              <p className="text-primary-600">Thanks for your feedback!</p>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => handleFeedback(true)}
                  className="btn-ghost text-sm"
                >
                  <HandThumbUpIcon className="w-5 h-5 mr-1" />
                  Yes
                </button>
                <button
                  onClick={() => handleFeedback(false)}
                  className="btn-ghost text-sm"
                >
                  <HandThumbDownIcon className="w-5 h-5 mr-1" />
                  No
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-900 dark:text-white">Help Center</h1>
          <p className="text-surface-600 dark:text-surface-400 mt-1">
            Find answers, browse documentation, or get support
          </p>
        </div>
        <button
          onClick={() => navigate('/tickets')}
          className="btn-secondary"
        >
          <TicketIcon className="w-5 h-5" />
          My Tickets
        </button>
      </div>
      
      {/* Search Bar */}
      <div className="card">
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search documentation or ask a question..."
              className="input pl-10 w-full"
            />
          </div>
          <button type="submit" className="btn-secondary">
            Search
          </button>
          <button
            type="button"
            onClick={handleAskAI}
            disabled={askLoading || !searchQuery.trim() || (aiUsageInfo?.remaining === 0)}
            className="btn-primary"
            title={aiUsageInfo?.remaining === 0 ? 'Daily limit reached' : ''}
          >
            {askLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <SparklesIcon className="w-5 h-5" />
                Ask AI
                {aiUsageInfo?.remaining !== null && aiUsageInfo?.remaining !== undefined && aiUsageInfo.remaining > 0 && (
                  <span className="ml-1 text-xs opacity-75">({aiUsageInfo.remaining})</span>
                )}
              </>
            )}
          </button>
        </form>
      </div>
      
      {/* AI Answer */}
      {aiAnswer && (
        <div className="card bg-gradient-to-r from-purple-500/5 to-pink-500/5 border-purple-500/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <SparklesIcon className="w-5 h-5 text-purple-500" />
              <h3 className="font-semibold text-purple-700 dark:text-purple-300">AI Answer</h3>
              {aiAnswer.cached && (
                <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded-full">
                  Cached
                </span>
              )}
            </div>
            {aiUsageInfo && aiUsageInfo.remaining !== null && (
              <span className="text-xs text-surface-500">
                {aiUsageInfo.remaining} / {aiUsageInfo.limit} questions remaining today
              </span>
            )}
          </div>
          <div className="prose dark:prose-invert max-w-none text-sm">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]} 
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
            >
              {aiAnswer.answer}
            </ReactMarkdown>
          </div>
          {aiAnswer.relatedDocs?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-purple-500/20">
              <p className="text-sm text-surface-500 mb-2">Related articles:</p>
              <div className="flex flex-wrap gap-2">
                {aiAnswer.relatedDocs.map(doc => (
                  <button
                    key={doc.slug}
                    onClick={() => handleDocClick(doc.slug)}
                    className="text-sm text-primary-600 hover:underline"
                  >
                    {doc.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Rate Limit Warning */}
      {aiUsageInfo && aiUsageInfo.remaining === 0 && (
        <div className="card bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-800/30 rounded-full">
              <SparklesIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h4 className="font-medium text-amber-800 dark:text-amber-200">
                Daily AI limit reached
              </h4>
              <p className="text-sm text-amber-600 dark:text-amber-400">
                You've used all {aiUsageInfo.limit} AI questions for today. 
                Your limit resets at midnight UTC. You can still search the documentation below.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Search Results */}
      {searchResults && (
        <div className="card">
          <h3 className="font-semibold text-surface-900 dark:text-white mb-4">
            Search Results ({searchResults.length})
          </h3>
          {searchResults.length === 0 ? (
            <p className="text-surface-500">No results found. Try asking AI or submit a support ticket.</p>
          ) : (
            <div className="space-y-3">
              {searchResults.map(doc => (
                <button
                  key={doc.slug}
                  onClick={() => handleDocClick(doc.slug)}
                  className="w-full text-left p-3 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
                >
                  <h4 className="font-medium text-surface-900 dark:text-white">{doc.title}</h4>
                  <p className="text-sm text-surface-500 line-clamp-2">{doc.excerpt}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Main Content */}
      {!searchResults && !aiAnswer && (
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1 space-y-4">
            {loading ? (
              <div className="card animate-pulse">
                <div className="h-6 bg-surface-200 dark:bg-surface-700 rounded mb-4 w-3/4" />
                <div className="space-y-2">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="h-10 bg-surface-100 dark:bg-surface-800 rounded" />
                  ))}
                </div>
              </div>
            ) : (
              categories.map(category => {
                const Icon = CATEGORY_ICONS[category.category] || DocumentTextIcon;
                return (
                  <div key={category.category} className="card">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className={`w-5 h-5 ${CATEGORY_COLORS[category.category]}`} />
                      <h3 className="font-semibold text-surface-900 dark:text-white">
                        {category.label}
                      </h3>
                    </div>
                    <div className="space-y-1">
                      {category.documents.map(doc => (
                        <button
                          key={doc.slug}
                          onClick={() => handleDocClick(doc.slug)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                            currentSlug === doc.slug
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                              : 'hover:bg-surface-50 dark:hover:bg-surface-800 text-surface-600 dark:text-surface-400'
                          }`}
                        >
                          <span className="truncate">{doc.title}</span>
                          <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          {/* Main Content Area */}
          <div className="lg:col-span-3">
            <div className="card min-h-[400px]">
              {renderDocumentContent()}
            </div>
            
            {/* Still need help? */}
            <div className="card mt-6 bg-gradient-to-r from-primary-500/10 to-purple-500/10 border-primary-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary-500/20 rounded-full">
                    <QuestionMarkCircleIcon className="w-8 h-8 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-surface-900 dark:text-white">
                      Still need help?
                    </h3>
                    <p className="text-sm text-surface-600 dark:text-surface-400">
                      Our support team is here to assist you
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTicketModal(true)}
                  className="btn-primary"
                >
                  <TicketIcon className="w-5 h-5" />
                  Submit a Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Ticket Modal */}
      <SubmitTicketModal
        isOpen={showTicketModal}
        onClose={() => setShowTicketModal(false)}
        onSuccess={(ticket) => {
          setShowTicketModal(false);
          toast.success('Ticket submitted successfully!');
          navigate(`/tickets/${ticket._id}`);
        }}
      />
    </div>
  );
}
